import React from 'react';
import Page from '../../containers/Page/Page';
import { Box, Button, IconButton, Theme, Typography, createStyles, makeStyles, Grid, TextField, FormControlLabel, Switch, InputAdornment, Dialog, AppBar, DialogTitle, DialogContent, DialogActions, Tooltip, Paper, InputBase, List, ListItem, ListItemAvatar, Avatar, ListItemText, Fade, LinearProgress, MenuItem } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { Add, ArrowBack, Save, Search } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { Helpers } from '../../utils/Helpers';
import { api } from '../../services/api';
import { Pagination } from '@material-ui/lab';
import Logger from '../../components/Logger';
import { ILog } from '../../types/Logger';

interface IBomba {
    codigo_sap: string;
    tamanho: string;
    tipo_material: string;
    modelo_id: string;
}

interface IModelo {
    id: string;
    nome: string;
}

interface IModeloData {
    data: IModelo[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

interface IDetalhe {
    id: string;
    campo: string;
}

interface ITemplate {
    id: string;
    template: string;
    tipo: string;
    detalhes?: IDetalhe[];
    valor?: string;
}

interface IParams {
    id: string;
}

function BombasCreate() {

    const history = useHistory();
    const classes = useStyles();
    const params = useParams<IParams>();

    const { enqueueSnackbar: alert } = useSnackbar();

    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [bomba, setBomba] = React.useState<IBomba>({} as IBomba);
    const [ativa, setAtiva] = React.useState<boolean>(true);
    const [modelo, setModelo] = React.useState<IModelo>({} as IModelo);
    const [modeloDialog, setModeloDialog] = React.useState<boolean>(false);
    const [modeloLoading, setModeloLoading] = React.useState<boolean>(false);
    const [modeloSearch, setModeloSearch] = React.useState<string>('');
    const [modelos, setModelos] = React.useState<IModeloData>();
    const [templates, setTemplates] = React.useState<ITemplate[]>([]);
    const [logs, setLogs] = React.useState<ILog[]>([]);

    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchModelos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleFetch() {
        if (params.id) {
            setLoading(true);
            try {
                const response = await api.get(`bombas/${params.id}`);
                setBomba(response.data.object);
                setAtiva(response.data.object.ativa);
                setModelo(response.data.object.modelo);
                setTemplates(response.data.object.modelo.templates);
                setLogs(response.data.object.logs);
                setError('');
            } catch (error: any) {
                setError(Helpers.errorFormat(error));
            }
            setLoading(false);
        }
    }

    async function handleFetchModelos(page: number = 1) {
        setModeloLoading(true);
        try {
            const response = await api.get(`modelos?search=${modeloSearch}&page=${page}&tipo=BOMBA&ativa=1`);
            setModelos(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        }
        setModeloLoading(false);
    }

    async function handleFetchTemplates(modelo: IModelo) {
        try {
            const response = await api.get(`modelos/${modelo.id}`);
            setTemplates(response.data.object.templates);
        } catch (error) {
            Helpers.error(error, alert);
        }
    }

    async function handleSave() {
        setLoading(true);
        try {
            const response = await api.request({
                url: params.id ? `bombas/${params.id}` : 'bombas',
                method: params.id ? 'PUT' : 'POST',
                data: { ...bomba, ativa, templates },
            });
            alert(response.data.message, { variant: 'success' });
            if (!params.id) { history.push(`/bombas/${response.data.id}`); }
        } catch (error) {
            Helpers.error(error, alert);
        }
        setLoading(false);
    }

    function handleModelosKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            handleFetchModelos();
        }
    }

    async function selectModelo(modelo: IModelo) {
        setModelo(modelo);
        setModeloDialog(false);
        setBomba({ ...bomba, modelo_id: modelo.id });
        await handleFetchTemplates(modelo);
    }

    function changeTemplate(template: ITemplate, value: string) {
        let index = templates.indexOf(template);
        let newTemplates = [...templates];
        newTemplates[index] = { ...template, valor: value };
        setTemplates(newTemplates);
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {params.id ? 'Editar Bomba' : 'Nova Bomba'}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    {params.id &&
                        <Box mr={2}>
                            <Logger
                                logs={logs}
                            />
                        </Box>
                    }
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => handleSave()}>
                        Salvar
                    </Button>
                </Box>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        {params.id &&
                            <Box mb={2} display="flex" justifyContent="end">
                                <FormControlLabel
                                    control={<Switch checked={Boolean(ativa)} onChange={(event) => setAtiva(event.target.checked)} name="checkedA" />}
                                    label="Ativo"
                                />
                            </Box>
                        }
                        <Grid className={classes.row} container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="codigo_sap"
                                    name="codigo_sap"
                                    label="Código SAP"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={bomba.codigo_sap || ''}
                                    onChange={(event) => setBomba({ ...bomba, codigo_sap: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="modelo_id"
                                    name="modelo_id"
                                    label="Modelo"
                                    variant="outlined"
                                    disabled={true}
                                    fullWidth={true}
                                    value={modelo.nome || ''}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title="Selecionar">
                                                    <IconButton color="primary" edge="end" onClick={() => setModeloDialog(true)}>
                                                        <Search />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Dialog open={modeloDialog} fullWidth={true} maxWidth="xs">
                                <AppBar style={{ position: 'relative' }}>
                                    <DialogTitle>Selecionar Modelo</DialogTitle>
                                    <Box mx={3} mb={3}>
                                        <Paper className={classes.custom}>
                                            <InputBase
                                                autoFocus
                                                disabled={loading}
                                                className={classes.input}
                                                placeholder="Pesquisa"
                                                onChange={(event) => setModeloSearch(event.target.value)}
                                                onKeyUp={(event) => handleModelosKeyUp(event)}
                                                value={modeloSearch}
                                            />
                                            <IconButton
                                                disabled={loading}
                                                style={{ padding: 10 }}
                                                onClick={() => handleFetchModelos()}
                                            >
                                                <Search />
                                            </IconButton>
                                        </Paper>
                                        <Box mt={2}>
                                            <Grid container justifyContent="center">
                                                <Pagination
                                                    page={modelos?.current_page || 1}
                                                    count={modelos?.last_page || 1}
                                                    onChange={(_event, page) => handleFetchModelos(page)}
                                                    color="secondary"
                                                />
                                            </Grid>
                                        </Box>
                                    </Box>
                                    <Fade in={modeloLoading} timeout={500}>
                                        <LinearProgress color="secondary"></LinearProgress>
                                    </Fade>
                                </AppBar>
                                <DialogContent>
                                    {modelos &&
                                        <List>
                                            {modelos.data && modelos.data.map((modelo, index) => (
                                                <ListItem button key={index} onClick={() => selectModelo(modelo)}>
                                                    <ListItemAvatar>
                                                        <Avatar>
                                                            <Add />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={modelo.nome}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    }
                                </DialogContent>
                                <DialogActions>
                                    <Button color="primary" onClick={() => setModeloDialog(false)}>
                                        Fechar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="tamanho"
                                    name="tamanho"
                                    label="Tamanho"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={bomba.tamanho || ''}
                                    onChange={(event) => setBomba({ ...bomba, tamanho: event.target.value })}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="tipo_material"
                                    name="tipo_material"
                                    label="Tipo de Material"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={bomba.tipo_material || ''}
                                    onChange={(event) => setBomba({ ...bomba, tipo_material: event.target.value })}
                                />
                            </Grid>
                            {templates && templates.map((template, index) => {
                                if (template.tipo === "numeric") {
                                    return (
                                        <Grid key={index} item xs={12} md={4}>
                                            <TextField
                                                label={template.template}
                                                variant="outlined"
                                                type="number"
                                                fullWidth={true}
                                                value={template.valor || ''}
                                                onChange={(event) => changeTemplate(template, event.target.value)}
                                            />
                                        </Grid>
                                    )
                                } else if (template.tipo === "date") {
                                    return (
                                        <Grid key={index} item xs={12} md={4}>
                                            <TextField
                                                label={template.template}
                                                variant="outlined"
                                                type="date"
                                                fullWidth={true}
                                                value={template.valor || ''}
                                                onChange={(event) => changeTemplate(template, event.target.value)}
                                            />
                                        </Grid>
                                    )
                                } else if (template.tipo === "dropdown") {
                                    return (
                                        <Grid key={index} item xs={12} md={4}>
                                            <TextField
                                                label={template.template}
                                                variant="outlined"
                                                select={true}
                                                fullWidth={true}
                                                value={template.valor || ''}
                                                onChange={(event) => changeTemplate(template, event.target.value)}
                                            >
                                                {template.detalhes && template.detalhes.map((detalhe, index) => (
                                                    <MenuItem key={index} value={detalhe.id}>{detalhe.campo}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    )
                                } else {
                                    return (
                                        <Grid key={index} item xs={12} md={4}>
                                            <TextField
                                                label={template.template}
                                                variant="outlined"
                                                fullWidth={true}
                                                value={template.valor || ''}
                                                onChange={(event) => changeTemplate(template, event.target.value)}
                                            />
                                        </Grid>
                                    )
                                }
                            })}
                        </Grid>
                    </Box>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
    })
);

export default BombasCreate;